/**
 * Build identity (DNKADM-0372). A single-page app that is never reloaded keeps
 * running the JavaScript it started with, forever — signing out and back in is
 * client-side routing and never unloads it. No cache header fixes that: `no-cache`
 * only helps when something re-requests the page, and a long-lived front-desk tab
 * never does. So the app learns its own identifier at build time and, from the
 * shell, periodically asks the server which identifier it is currently serving.
 *
 * Two sides of one comparison:
 *  - {@link getBuildId} — the identifier compiled into THIS bundle
 *    (`__BUILD_ID__`, defined by `vite.config.ts`): `YYYYMMDD.<sha7>` on a
 *    SHA-backed build, `local-<ISO>` on a hand build. `null` when the build is
 *    unidentifiable (dev server, tests): the check is then disabled outright,
 *    never run with two blanks that would compare "equal".
 *  - {@link fetchServedBuildId} — the identifier the server publishes in
 *    `version.json` (emitted next to `index.html` by the same build). Fetched
 *    with `cache: 'no-store'` AND a cache-busting query — the whole defect is
 *    that caches lie, so this one file trusts no header.
 *
 * Failure is silent by design: a fetch that rejects, a non-2xx status, or a
 * malformed body all resolve to `null` (one `console.debug` line, nothing else).
 * A check that renders its own failure as a problem is indistinguishable from
 * the problem it watches for (the DNKADM-0333 lesson) — if we cannot tell, we say
 * nothing.
 */
import { env } from './env';

/** The identifier compiled into this bundle, or `null` when the build has none. */
export function getBuildId(): string | null {
  const raw = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__.trim() : '';
  return raw.length > 0 ? raw : null;
}

/**
 * The identifier the server currently serves in `version.json`, or `null` on any
 * failure (network error, non-2xx, malformed/unshaped JSON). Never throws.
 */
export async function fetchServedBuildId(): Promise<string | null> {
  try {
    const url = `${env.appOrigin}/version.json?t=${Date.now()}`;
    const response = await fetch(url, { cache: 'no-store', credentials: 'omit' });
    if (!response.ok) {
      console.debug(`[buildId] version.json returned ${response.status}; ignoring.`);
      return null;
    }
    const body: unknown = await response.json();
    const id =
      body && typeof body === 'object' ? (body as { buildId?: unknown }).buildId : undefined;
    if (typeof id !== 'string' || id.trim().length === 0) {
      console.debug('[buildId] version.json carried no buildId; ignoring.');
      return null;
    }
    return id.trim();
  } catch {
    console.debug('[buildId] version.json could not be fetched or parsed; ignoring.');
    return null;
  }
}
